import { ArrowRightOnRectangle, BarsThree, MagnifyingGlass, SidebarLeft, User } from "@medusajs/icons"
import { useIssueCounts } from "@/lib/issues"
import { useInboxCounts } from "@/lib/cs-inbox"
import { AlertsBell, EnablePushPrompt } from "@/components/staff-alerts"
import { clx, DropdownMenu, Text } from "@medusajs/ui"
import { useQuery } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom"
import { logout } from "@/lib/api"
import { fullName } from "@/lib/format"
import { activeNav, NAV, SALES_CHANNELS, SETTINGS_ITEM, type NavItem, type SubItem } from "@/lib/nav"
import { useCan, useCurrentMe } from "@/lib/permissions"
import { sdk } from "@/lib/sdk"
import { CommandPalette } from "./command-palette"
import { APPS_HEADER, enabledAppNav, isAppOn } from "@/apps/registry"
import { KeelLockup, KeelMark, usePageTitle } from "@/keel/logo"

const SHELL_LEFT = "15rem"
const SHELL_LEFT_COLLAPSED = "4rem"
/** Per-device preference only (a new key; nothing else reads it). */
const COLLAPSED_KEY = "keel.sidebar.collapsed"

function readCollapsed() {
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === "1"
  } catch {
    return false
  }
}

/** Desktop sidebar: full width with the KEEL lockup, or a 64px icon rail with the K mark. */
function useCollapsed() {
  const [collapsed, set] = useState(readCollapsed)
  const toggle = () =>
    set((c) => {
      try {
        window.localStorage.setItem(COLLAPSED_KEY, c ? "0" : "1")
      } catch {
        // Private mode / blocked storage: the choice just isn't remembered.
      }
      return !c
    })
  return [collapsed, toggle] as const
}

/** "Orders", "Drafts", "Settings"…: the label of the nav entry the page sits under, for the tab title. */
export function navLabel(pathname: string, items: SubItem[]) {
  let best: { label: string; len: number } | null = null
  const consider = (label: string, to: string) => {
    const hit = to === "/" ? pathname === "/" : pathname === to || pathname.startsWith(to + "/")
    if (hit && (!best || to.length > best.len)) best = { label, len: to.length }
  }
  for (const i of items) {
    consider(i.label, i.to)
    ;(i as NavItem).children?.forEach((c) => consider(c.label, c.to))
  }
  return (best as { label: string } | null)?.label ?? null
}

/** Open orders not yet fulfilled: the number on the Orders nav item. */
function useOpenOrderCount(enabled: boolean) {
  return useQuery({
    queryKey: ["nav", "open-orders"],
    enabled,
    staleTime: 60_000,
    refetchInterval: 120_000,
    queryFn: async () => (await sdk.admin.order.list({ status: ["pending"], limit: 1, fields: "id" } as any)).count,
  })
}

function SidebarLink({ item, active, badge, alert, collapsed, onNavigate }: { item: NavItem; active: boolean; badge?: number; alert?: boolean; collapsed?: boolean; onNavigate: () => void }) {
  const Icon = item.icon
  return (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      onClick={onNavigate}
      data-active={active}
      title={collapsed ? item.label : undefined}
      aria-label={collapsed ? (badge ? `${item.label}, ${badge}` : item.label) : undefined}
      className={clx("k-nav-row", collapsed && "relative justify-center px-0")}
    >
      <Icon />
      {collapsed ? (
        badge ? <span aria-hidden className={clx("absolute right-1.5 top-1.5 size-1.5 rounded-full", alert ? "bg-[var(--k-alert)]" : "bg-[var(--k-blue)]")} /> : null
      ) : (
        <>
          <span className="flex-1 truncate">{item.label}</span>
          {badge ? (
            <span className="k-nav-count" data-alert={alert ? "true" : undefined}>
              {badge}
            </span>
          ) : null}
        </>
      )}
    </NavLink>
  )
}

function SidebarSub({ item, onNavigate }: { item: SubItem; onNavigate: () => void }) {
  return (
    <NavLink to={item.to} end onClick={onNavigate} className="k-nav-sub">
      {item.label}
    </NavLink>
  )
}

export function Sidebar({ onNavigate = () => {}, onSearch, collapsed = false, onToggleCollapsed }: { onNavigate?: () => void; onSearch: () => void; collapsed?: boolean; onToggleCollapsed?: () => void }) {
  const me = useCurrentMe()
  const can = useCan()
  const { pathname } = useLocation()
  const allowed = (i: SubItem) => (!i.perm || can(i.perm)) && (!i.app || isAppOn(me, i.app))
  const nav = NAV.filter(allowed)
  const apps = enabledAppNav(me).filter(allowed)
  const current = activeNav(pathname, [...nav, ...SALES_CHANNELS, ...apps, SETTINGS_ITEM])
  const openOrders = useOpenOrderCount(can("orders:read"))
  const issues = useIssueCounts(can("orders:read"))
  const inbox = useInboxCounts(can("customers:read"))
  const name = fullName(me.user) || me.user.email

  if (collapsed) {
    return (
      <CollapsedRail
        nav={nav}
        apps={apps}
        current={current}
        openOrders={openOrders.data}
        issues={issues.data}
        newMessages={inbox.data?.new}
        onSearch={onSearch}
        onExpand={onToggleCollapsed}
        name={name}
        email={me.user.email}
        storeName={me.store.name}
      />
    )
  }

  return (
    <div className="flex h-full flex-col border-r border-[var(--k-line)] bg-[var(--k-surface)] px-3 pb-2 pt-3 text-[var(--k-ink)]">
      <div className="mb-3 flex items-center justify-between gap-x-2">
        <NavLink to="/" onClick={onNavigate} aria-label="Keel home" className="-ml-1 block">
          <KeelLockup width={104} />
        </NavLink>
        <div className="flex items-center gap-x-0.5">
          <AlertsBell className="hidden h-8 px-1.5 text-[var(--k-ink-muted)] hover:text-[var(--k-ink)] lg:block" onOpen={onNavigate} />
          {onToggleCollapsed ? (
            <button
              type="button"
              onClick={onToggleCollapsed}
              className="hidden rounded-[var(--k-r-sm)] p-1.5 text-[var(--k-ink-muted)] hover:bg-[var(--k-sunken)] hover:text-[var(--k-ink)] lg:block"
              aria-label="Collapse sidebar"
              title="Collapse sidebar"
            >
              <SidebarLeft />
            </button>
          ) : null}
        </div>
      </div>
      <button onClick={onSearch} className="k-sidebar-search mb-3">
        <MagnifyingGlass />
        <span className="flex-1">Search</span>
        <kbd className="k-kbd">⌘K</kbd>
      </button>

      <nav className="flex flex-1 flex-col gap-y-0.5 overflow-y-auto">
        {nav.map((item) => {
          const active = current === item
          return (
            <div key={item.to}>
              <SidebarLink item={item} active={active && pathname === item.to} badge={item.badge === "orders" ? openOrders.data : item.badge === "issues" ? issues.data?.open : item.badge === "messages" ? inbox.data?.new : undefined} alert={item.badge === "issues" && !!issues.data?.critical} onNavigate={onNavigate} />
              {active && item.children?.filter(allowed).length ? (
                <div className="mb-1 mt-0.5 flex flex-col gap-y-0.5">
                  {item.children.filter(allowed).map((c) => (
                    <SidebarSub key={c.to} item={c} onNavigate={onNavigate} />
                  ))}
                </div>
              ) : null}
            </div>
          )
        })}

        <div className="k-eyebrow mt-4 px-2 pb-1">Sales channels</div>
        {SALES_CHANNELS.map((item) => (
          <SidebarLink key={item.to} item={item} active={current === item} onNavigate={onNavigate} />
        ))}

        {/* Apps switched on in Settings → Apps, as in Shopify's sidebar. */}
        {apps.length ? (
          <>
            <NavLink to={APPS_HEADER.to} onClick={onNavigate} className="k-eyebrow mt-4 rounded-[var(--k-r-sm)] px-2 pb-1 hover:text-[var(--k-ink)]">
              Apps
            </NavLink>
            {apps.map((item) => {
              const active = current === item
              return (
                <div key={item.to}>
                  <SidebarLink item={item} active={active && pathname === item.to} onNavigate={onNavigate} />
                  {active && item.children?.filter(allowed).length ? (
                    <div className="mb-1 mt-0.5 flex flex-col gap-y-0.5">
                      {item.children.filter(allowed).map((c) => (
                        <SidebarSub key={c.to} item={c} onNavigate={onNavigate} />
                      ))}
                    </div>
                  ) : null}
                </div>
              )
            })}
          </>
        ) : null}
      </nav>

      <div className="mt-2 flex flex-col gap-y-0.5 border-t border-[var(--k-line)] pt-2">
        <SidebarLink item={SETTINGS_ITEM} active={current === SETTINGS_ITEM} onNavigate={onNavigate} />
        <StoreMenu name={name} email={me.user.email} storeName={me.store.name} onNavigate={onNavigate} />
      </div>
    </div>
  )
}

/** The store / account menu at the foot of the sidebar. */
function StoreMenu({ name, email, storeName, onNavigate, compact = false }: { name: string; email: string; storeName: string; onNavigate: () => void; compact?: boolean }) {
  return (
    <DropdownMenu>
      <DropdownMenu.Trigger asChild>
        <button
          className={clx("flex h-10 w-full items-center gap-x-2 rounded-[var(--k-r-sm)] px-2 text-left hover:bg-[var(--k-sunken)]", compact && "justify-center px-0")}
          aria-label={compact ? `${storeName}: account menu` : undefined}
          title={compact ? storeName : undefined}
        >
          <span className="flex size-6 shrink-0 items-center justify-center rounded-[var(--k-r-sm)] bg-[var(--k-hull)] text-[12px] font-semibold text-white">{storeName[0]?.toUpperCase() ?? "K"}</span>
          {compact ? null : <span className="min-w-0 flex-1 truncate text-[13px] font-medium">{storeName}</span>}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="start" side="top" className="min-w-[220px]">
        <DropdownMenu.Label>
          <Text size="small" weight="plus">{name}</Text>
          <Text size="xsmall" className="text-ui-fg-muted">{email}</Text>
        </DropdownMenu.Label>
        <DropdownMenu.Separator />
        <DropdownMenu.Item asChild className="gap-x-2">
          <NavLink to="/settings/account" onClick={onNavigate}>
            <User /> Your account
          </NavLink>
        </DropdownMenu.Item>
        <DropdownMenu.Item onClick={() => logout()} className="gap-x-2">
          <ArrowRightOnRectangle /> Log out
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu>
  )
}

/** Collapsed desktop sidebar: the K mark, then icon-only rows (labels in tooltips). */
function CollapsedRail({
  nav,
  apps,
  current,
  openOrders,
  issues,
  newMessages,
  onSearch,
  onExpand,
  name,
  email,
  storeName,
}: {
  nav: NavItem[]
  apps: NavItem[]
  current: NavItem | null
  openOrders?: number
  issues?: { open?: number; critical?: number }
  newMessages?: number
  onSearch: () => void
  onExpand?: () => void
  name: string
  email: string
  storeName: string
}) {
  const none = () => {}
  return (
    <div className="flex h-full flex-col border-r border-[var(--k-line)] bg-[var(--k-surface)] px-2 pb-2 pt-3">
      <NavLink to="/" aria-label="Keel home" className="mx-auto mb-2 block">
        <KeelMark size={36} />
      </NavLink>
      <button type="button" onClick={onExpand} className="k-nav-row justify-center px-0" aria-label="Expand sidebar" title="Expand sidebar">
        <SidebarLeft className="rotate-180" />
      </button>
      <button type="button" onClick={onSearch} className="k-nav-row justify-center px-0" aria-label="Search" title="Search (⌘K)">
        <MagnifyingGlass />
      </button>
      <AlertsBell className="k-nav-row mb-2 flex justify-center px-0" />
      <nav className="flex flex-1 flex-col gap-y-0.5 overflow-y-auto border-t border-[var(--k-line)] pt-2">
        {[...nav, ...SALES_CHANNELS, ...apps].map((item) => (
          <SidebarLink
            key={item.to}
            item={item}
            collapsed
            active={current === item}
            badge={item.badge === "orders" ? openOrders : item.badge === "issues" ? issues?.open : item.badge === "messages" ? newMessages : undefined}
            alert={item.badge === "issues" && !!issues?.critical}
            onNavigate={none}
          />
        ))}
      </nav>
      <div className="mt-2 flex flex-col gap-y-0.5 border-t border-[var(--k-line)] pt-2">
        <SidebarLink item={SETTINGS_ITEM} collapsed active={current === SETTINGS_ITEM} onNavigate={none} />
        <StoreMenu compact name={name} email={email} storeName={storeName} onNavigate={none} />
      </div>
    </div>
  )
}

/** ⌘K / Ctrl+K anywhere opens search. */
export function useSearchShortcut() {
  const [open, setOpen] = useState(false)
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault()
        setOpen((o) => !o)
      }
    }
    window.addEventListener("keydown", h)
    return () => window.removeEventListener("keydown", h)
  }, [])
  return [open, setOpen] as const
}

/** Keel layout: white sidebar (KEEL lockup, search, nav, store menu) beside a white workspace, divided by a line. */
export function AppShell() {
  const me = useCurrentMe()
  const can = useCan()
  const [mobile, setMobile] = useState(false)
  const [collapsed, toggleCollapsed] = useCollapsed()
  const [search, setSearch] = useSearchShortcut()
  const location = useLocation()
  const navigate = useNavigate()
  usePageTitle(navLabel(location.pathname, [...NAV, ...SALES_CHANNELS, ...enabledAppNav(me), SETTINGS_ITEM].filter((i) => !i.perm || can(i.perm))))

  return (
    <div className="flex min-h-screen bg-[var(--k-surface)] lg:h-screen lg:overflow-hidden" style={{ ["--tf-content-left" as any]: collapsed ? SHELL_LEFT_COLLAPSED : SHELL_LEFT }}>
      <aside className={clx("sticky top-0 hidden h-screen shrink-0 lg:block", collapsed ? "w-16" : "w-60")}>
        <Sidebar onSearch={() => setSearch(true)} collapsed={collapsed} onToggleCollapsed={toggleCollapsed} />
      </aside>

      {mobile && (
        <div className="fixed inset-0 z-40 lg:hidden" onClick={() => setMobile(false)}>
          <div className="absolute inset-0 bg-[rgba(11,26,36,0.4)]" />
          <aside className="absolute inset-y-0 left-0 w-64 shadow-[var(--tb-shadow-pop)]" onClick={(e) => e.stopPropagation()}>
            <Sidebar
              onNavigate={() => setMobile(false)}
              onSearch={() => {
                setMobile(false)
                setSearch(true)
              }}
            />
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col lg:h-screen">
        <header className="sticky top-0 z-20 flex items-center gap-x-2 border-b border-[var(--k-line)] bg-[var(--k-surface)] px-3 py-2 text-[var(--k-ink)] lg:hidden">
          <button onClick={() => setMobile(true)} className="rounded-[var(--k-r-sm)] p-1.5 text-[var(--k-ink-muted)] hover:bg-[var(--k-sunken)]" aria-label="Open menu">
            <BarsThree />
          </button>
          <NavLink to="/" aria-label="Keel home" className="shrink-0">
            <KeelMark size={28} />
          </NavLink>
          <span className="min-w-0 flex-1 truncate text-[13px] font-medium">{me.store.name}</span>
          <button onClick={() => setSearch(true)} className="rounded-[var(--k-r-sm)] p-1.5 text-[var(--k-ink-muted)] hover:bg-[var(--k-sunken)]" aria-label="Search">
            <MagnifyingGlass />
          </button>
          <AlertsBell className="p-1.5 text-[var(--k-ink-muted)]" />
        </header>
        <div className="relative min-h-0 flex-1 overflow-y-auto bg-[var(--k-surface)]">
          <main key={location.pathname} className="mx-auto w-full max-w-[1200px] px-4 py-6 lg:px-8">
            <Outlet />
          </main>
        </div>
      </div>

      <CommandPalette open={search} onOpenChange={setSearch} onGo={(to) => navigate(to)} />
      <EnablePushPrompt />
    </div>
  )
}
